import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { gsap } from 'gsap';
import { Gamepad2, AlertCircle, RefreshCw, Shield, BarChart3, Download, Sparkles, TrendingUp, Plus, Users, Calendar } from 'lucide-react';
import Layout from '../components/layout/Layout';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import LineChart from '../components/ui/LineChart';
import PermissionGuard from '../components/ui/PermissionGuard';
import AddGameModal from '../components/modals/AddGameModal';
import { apiService, Game, AggregatedRegistration, CreateGameRequest, GameStatistics } from '../services/api';
import { usePermissions } from '../contexts/PermissionContext';
import { roleToCompany, createUrlSafeString } from '../utils/roleUtils';

const GamesPage: React.FC = () => {
  const { company } = useParams<{ company: string }>();
  const { roles, isLoading: permissionsLoading } = usePermissions();
  // Removed useApiWithToasts to prevent race conditions - using apiService directly
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [aggregatedRegistrations, setAggregatedRegistrations] = useState<AggregatedRegistration[]>([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState<string | null>(null);
  const [showAddGameModal, setShowAddGameModal] = useState(false);
  const [addGameLoading, setAddGameLoading] = useState(false);
  const [apiReady, setApiReady] = useState(false);
  const [gameStatistics, setGameStatistics] = useState<Map<string, GameStatistics>>(new Map());
  const [statsLoading, setStatsLoading] = useState(false);
  const cardsRef = useRef<HTMLDivElement[]>([]);
  
  // Use company from URL params - ensure lowercase for consistency
  const currentCompany = (company || 'unknown').toLowerCase();
  
  // Debug logging to track company parameter
  console.log('🏢 GamesPage company param:', { company, currentCompany, urlPath: window.location.pathname });
  
  // Check if user has access to this company
  const userCompanies = roles.map(role => roleToCompany(role));
  const hasAccessToCompany = userCompanies.includes(currentCompany);
  console.log('🔐 Access check:', { currentCompany, userCompanies, hasAccessToCompany });
  
  // Generate display name for the company
  const companyDisplayName = currentCompany.charAt(0).toUpperCase() + currentCompany.slice(1);

  const fetchGameStatistics = useCallback(async (gamesList: Game[]) => {
    if (gamesList.length === 0) return;

    try {
      setStatsLoading(true);
      console.log(`📈 Fetching statistics for ${gamesList.length} games`);
      
      // Fetch statistics for all games in parallel
      const statisticsPromises = gamesList.map(game => 
        apiService.getGameStatistics(game.game, game.game_type, currentCompany)
          .catch(error => {
            console.warn(`Failed to fetch statistics for ${game.game}:`, error);
            return {
              game: game.game,
              game_type: game.game_type,
              totalPlayers: 0,
              todayPlayers: 0,
              lastUpdateDate: ''
            } as GameStatistics;
          })
      );

      const allStatistics = await Promise.all(statisticsPromises);
      
      // Create a map for easy lookup by game key
      const statsMap = new Map<string, GameStatistics>();
      allStatistics.forEach(stats => {
        const key = `${stats.game}-${stats.game_type}`;
        statsMap.set(key, stats);
      });

      setGameStatistics(statsMap);
      console.log(`✅ Successfully loaded statistics for ${allStatistics.length} games`);
    } catch (err: any) {
      console.error('Error fetching game statistics:', err);
      // Don't show error to user for statistics - just keep empty stats
    } finally {
      setStatsLoading(false);
    }
  }, [currentCompany]);

  const fetchGames = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setHasAnimated(false); // Reset animation state for new data
      console.log(`🎯 Making API call to getGames with company: "${currentCompany}"`);
      const gamesData = await apiService.getGames(currentCompany);
      setGames(gamesData || []); // Ensure we always have an array
      
      // Fetch statistics for the games after loading them
      if (gamesData && gamesData.length > 0) {
        fetchGameStatistics(gamesData);
      }
    } catch (err: any) {
      console.error('Error fetching games:', err);
      setError(
        err.response?.status === 401 
          ? 'Unauthorized access. Please check your permissions.'
          : err.response?.status === 403
          ? 'Forbidden. You do not have permission to view games.'
          : err.message?.includes('Authentication required')
          ? 'Please log in to view games.'
          : 'Failed to load games. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }, [currentCompany, fetchGameStatistics]);

  const fetchAggregatedRegistrations = useCallback(async () => {
    try {
      setChartLoading(true);
      setChartError(null);
      console.log(`📊 Fetching aggregated registrations for company: "${currentCompany}"`);
      const aggregatedData = await apiService.getAggregatedRegistrations(currentCompany);
      setAggregatedRegistrations(aggregatedData || []);
    } catch (err: any) {
      console.error('Error fetching aggregated registrations:', err);
      setChartError(
        err.response?.status === 401 
          ? 'Unauthorized access to registration data.'
          : err.response?.status === 403
          ? 'Forbidden. You do not have permission to view registration data.'
          : 'Failed to load registration chart data.'
      );
    } finally {
      setChartLoading(false);
    }
  }, [currentCompany]);

  // Monitor API service token state
  useEffect(() => {
    const checkApiReady = () => {
      const hasToken = apiService.hasAuthToken();
      setApiReady(hasToken);
      return hasToken;
    };

    // Check immediately
    checkApiReady();

    // Set up an interval to periodically check if API has token
    const interval = setInterval(() => {
      if (!apiReady && hasAccessToCompany && !permissionsLoading) {
        checkApiReady();
      }
    }, 100); // Check every 100ms

    return () => clearInterval(interval);
  }, [hasAccessToCompany, permissionsLoading, apiReady]);

  useEffect(() => {
    // Only fetch games when:
    // 1. Permissions are loaded (not loading)
    // 2. User has access to the current company
    // 3. Company is not 'unknown'
    // 4. API service has auth token
    if (!permissionsLoading && hasAccessToCompany && currentCompany !== 'unknown' && apiReady) {
      console.log('✅ Conditions met, fetching games for company:', currentCompany);
      fetchGames();
      fetchAggregatedRegistrations();
    } else {
      console.log('⏳ Waiting for conditions:', { 
        permissionsLoading, 
        hasAccessToCompany, 
        currentCompany,
        apiReady,
        willFetch: !permissionsLoading && hasAccessToCompany && currentCompany !== 'unknown' && apiReady
      });
    }
  }, [currentCompany, permissionsLoading, hasAccessToCompany, apiReady, fetchGames, fetchAggregatedRegistrations]);

  useEffect(() => {
    // Only animate when games first load and haven't been animated yet
    if (games.length > 0 && cardsRef.current.length > 0 && !hasAnimated && !loading) {
      // Set initial state for cards that don't have the CSS class yet
      gsap.set(cardsRef.current, {
        opacity: 0,
        y: 20,
        scale: 0.95
      });

      // Animate in with improved timing and easing
      gsap.to(cardsRef.current, {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.5,
        stagger: {
          amount: 0.3, // Total time for all cards to stagger
          from: "start"
        },
        ease: 'power3.out',
        onComplete: () => {
          setHasAnimated(true);
        }
      });
    }
  }, [games, hasAnimated, loading]);

  // Check if URL needs to be redirected to lowercase (after all hooks)
  if (company && company !== company.toLowerCase()) {
    console.log(`🔄 Redirecting from /games/${company} to /games/${company.toLowerCase()}`);
    return <Navigate to={`/games/${company.toLowerCase()}`} replace />;
  }

  const handleRetry = () => {
    fetchGames();
    fetchAggregatedRegistrations();
  };

  const handleAddGame = async (gameData: { game: string; game_type: string; icon_url?: string }) => {
    try {
      setAddGameLoading(true);
      
      const createGameRequest: CreateGameRequest = {
        ...gameData,
        company: currentCompany
      };
      
      console.log('🎮 Creating new game:', createGameRequest);
      
      const newGame = await apiService.createGame(createGameRequest);
      
      // Add the new game to the current games list
      const updatedGames = [...games, newGame];
      setGames(updatedGames);
      
      // Fetch statistics for the new game
      fetchGameStatistics([newGame]);
      
      // Close the modal
      setShowAddGameModal(false);
      
      // Optionally refresh aggregated registrations
      fetchAggregatedRegistrations();
      
      console.log('✅ Game created successfully:', newGame);
    } catch (err: any) {
      console.error('❌ Error creating game:', err);
      // Error handling is already done in the API service
      // The error will be thrown and can be caught by the modal if needed
      throw err;
    } finally {
      setAddGameLoading(false);
    }
  };

  // Calculate chart statistics
  const totalRegistrations = aggregatedRegistrations.reduce((sum, reg) => sum + reg.totalPlayers, 0);
  const averagePerDay = aggregatedRegistrations.length > 0 ? Math.round(totalRegistrations / aggregatedRegistrations.length) : 0;

  // Show welcome screen for unknown company route
  if (currentCompany === 'unknown') {
    return (
      <Layout title="Games Admin Dashboard">
        <div className="space-y-8">
          {/* Welcome Header */}
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="relative">
                <Gamepad2 className="w-20 h-20 text-primary-600 dark:text-primary-400" />
                <Sparkles className="w-8 h-8 text-yellow-500 absolute -top-2 -right-2 animate-pulse" />
              </div>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Welcome to Games Admin
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Your central hub for managing game registrations and analytics
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="card p-6 text-center">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Registration Analytics
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Track daily registrations, player counts, and engagement metrics across all your games
              </p>
            </div>

            <div className="card p-6 text-center">
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Download className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Data Export
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Download detailed CSV reports for specific dates or custom date ranges
              </p>
            </div>

            <div className="card p-6 text-center">
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Shield className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Secure Access
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Role-based permissions ensure you only see data you're authorized to access
              </p>
            </div>
          </div>

          {/* Available Companies */}
          {userCompanies.length > 0 && (
            <div className="max-w-2xl mx-auto">
              <div className="card p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 text-center">
                  Your Available Organizations
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {userCompanies.map((userCompany) => (
                    <Link
                      key={userCompany}
                      to={`/games/${userCompany}`}
                      className="p-4 border border-gray-200 dark:border-dark-700 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors text-center"
                    >
                      <Gamepad2 className="w-8 h-8 mx-auto mb-2 text-primary-600 dark:text-primary-400" />
                      <h4 className="font-medium text-gray-900 dark:text-gray-100 capitalize">
                        {userCompany}
                      </h4>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* No Access Notice */}
          {userCompanies.length === 0 && (
            <div className="max-w-2xl mx-auto">
              <div className="card p-6 border-l-4 border-yellow-500">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-1">
                      No Organizations Available
                    </h4>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">
                      You don't currently have access to any game organizations. 
                      Please contact your administrator to get access.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* How It Works */}
          <div className="max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6 text-center">
              How Games Admin Works
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">1</span>
                </div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Select Your Organization
                </h4>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  Choose from your available organizations to view their game data and analytics
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-green-600 dark:text-green-400">2</span>
                </div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Browse Games
                </h4>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  View all available games for your organization and access detailed registration data
                </p>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Access control check - show access denied if user doesn't have access to this company
  if (!hasAccessToCompany && currentCompany !== 'unknown') {
    return (
      <Layout title={`${companyDisplayName} Games`}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center p-8 card max-w-md">
            <AlertCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Access Denied
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              You don't have permission to access {companyDisplayName} games.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Available companies: {userCompanies.join(', ')}
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  if (loading || permissionsLoading || !apiReady) {
    return (
      <Layout title={`${companyDisplayName} Games`}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <LoadingSpinner size="large" />
            <p className="mt-4 text-gray-600 dark:text-gray-400">
              {permissionsLoading 
                ? 'Loading permissions...' 
                : !apiReady 
                ? 'Setting up API connection...'
                : 'Loading games...'}
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout title={`${companyDisplayName} Games`}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center p-8 card max-w-md">
            <AlertCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Error Loading Games
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {error}
            </p>
            <button
              onClick={handleRetry}
              className="btn-primary"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={`${companyDisplayName} Games`}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {companyDisplayName} Games
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Manage and view game registrations
            </p>
          </div>
          <div className="flex items-center gap-3">
            <PermissionGuard permissions={['create:games']} showFallback={false}>
              <button
                onClick={() => setShowAddGameModal(true)}
                className="btn-primary"
                disabled={loading || addGameLoading}
              >
                <Plus className="w-4 h-4" />
                Add New Game
              </button>
            </PermissionGuard>
            <button
              onClick={handleRetry}
              className="btn-secondary"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Registration Trends Chart */}
        <div className="card">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Registration Trends
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Daily registrations across all games
                  </p>
                </div>
              </div>
              {!chartLoading && !chartError && aggregatedRegistrations.length > 0 && (
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {totalRegistrations.toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Total registrations
                  </p>
                </div>
              )}
            </div>
          </div>
          
          <div className="p-6">
            {chartLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <LoadingSpinner size="large" />
                  <p className="mt-4 text-gray-600 dark:text-gray-400">
                    Loading chart data...
                  </p>
                </div>
              </div>
            ) : chartError ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    {chartError}
                  </p>
                  <button
                    onClick={fetchAggregatedRegistrations}
                    className="btn-secondary text-sm"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Retry
                  </button>
                </div>
              </div>
            ) : aggregatedRegistrations.length === 0 ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <BarChart3 className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">
                    No registration data available yet
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <LineChart
                  data={aggregatedRegistrations.map(reg => ({
                    date: reg.date,
                    value: reg.totalPlayers
                  }))}
                  title="Daily Registrations"
                  height={300}
                  className="mb-4"
                />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200 dark:border-dark-700">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {aggregatedRegistrations.length}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Days with data
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {averagePerDay.toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Avg. per day
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                      {Math.max(...aggregatedRegistrations.map(r => r.totalPlayers)).toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Peak day
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Games Grid */}
        {games.length === 0 ? (
          <div className="text-center py-12">
            <Gamepad2 className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              No Games Available
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              No games have been added for {companyDisplayName} yet.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Games will appear here once they are configured for this company.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {games.map((game, index) => {
              const gameKey = `${game.game}-${game.game_type}`;
              const stats = gameStatistics.get(gameKey);
              const isStatsLoading = statsLoading && !stats;
              
              return (
                <Link
                  key={gameKey}
                  to={`/games/${currentCompany}/${createUrlSafeString(game.game)}/${createUrlSafeString(game.game_type)}`}
                >
                  <div
                    ref={el => cardsRef.current[index] = el!}
                    className="card game-card-enter hover:shadow-lg cursor-pointer group hover:-translate-y-1 transition-shadow duration-300"
                  >
                    {/* Game Header */}
                    <div className="flex items-center gap-4 mb-4">
                      <div className="flex-shrink-0">
                        {game.icon_url ? (
                          <img
                            src={game.icon_url}
                            alt={`${game.game} icon`}
                            className="w-12 h-12 rounded-lg object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              target.nextElementSibling?.classList.remove('hidden');
                            }}
                          />
                        ) : null}
                        <div className={`w-12 h-12 bg-primary-100 dark:bg-primary-900/20 rounded-lg flex items-center justify-center ${game.icon_url ? 'hidden' : ''}`}>
                          <Gamepad2 className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                          {game.game}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">
                          {game.game_type}
                        </p>
                      </div>
                    </div>

                    {/* Statistics */}
                    <div className="space-y-3 mb-4">
                      {isStatsLoading ? (
                        <div className="flex items-center justify-center py-2">
                          <LoadingSpinner size="small" />
                          <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                            Loading stats...
                          </span>
                        </div>
                      ) : stats ? (
                        <>
                          {/* Total Players */}
                          <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-dark-700/50 rounded-lg">
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Total Players
                              </span>
                            </div>
                            <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
                              {stats.totalPlayers.toLocaleString()}
                            </span>
                          </div>

                          {/* Today Players */}
                          <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-dark-700/50 rounded-lg">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-green-600 dark:text-green-400" />
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Today
                              </span>
                            </div>
                            <span className="text-sm font-bold text-green-600 dark:text-green-400">
                              {stats.todayPlayers.toLocaleString()}
                            </span>
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-2">
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            No statistics available
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* Footer */}
                    <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-dark-700">
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        View Details
                      </span>
                      <div className="w-5 h-5 text-gray-400 group-hover:text-primary-600 dark:group-hover:text-primary-400 transform group-hover:translate-x-1 transition-all">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
        </div>

        {/* Add Game Modal */}
        <AddGameModal
          isOpen={showAddGameModal}
          onClose={() => setShowAddGameModal(false)}
          onSubmit={handleAddGame}
          loading={addGameLoading}
        />
    </Layout>
  );
};

export default GamesPage;
